<template>
  <div
    class="p-6 relative bg-white col-span-12 md:col-span-9 rounded-xl border border-neutral-200 shadow-lg mx-3 md:mx-0 flex flex-col md:flex-row items-start gap-6 lg:min-h-64 overflow-hidden"
  >
    <!-- Image overlapping the card -->
    <div class="absolute top-20 md:top-1/2 right-0 md:right-8 -translate-y-1/2 w-40 md:w-64">
      <img :src="require('@/assets/images/banner.png')" alt="Doctor Illustration" class="w-full h-auto object-contain" />
    </div>

    <!-- Text content -->
    <div class="flex-1 md:pr-72">
      <!-- Added md:pr-72 to prevent overlap -->
      <h2 class="text-xl md:text-3xl font-bold text-gray-800 mb-3">Let’s Get Started</h2>

      <!-- Short text for mobile -->
      <p class="text-gray-600 text-sm mb-6 block md:hidden">
        Hi there! How can I help
        <span class="break-small-screen">you today?</span>
      </p>
      <!-- Long text for desktop -->
      <p class="text-gray-600 text-sm mb-6 hidden md:block">Hi there, it’s great to connect with you. I’m here to support your health — how can I help you today?</p>

      <!-- Button aligned left -->
      <button
        @click="confirmConsult()"
        :disabled="loading || ifHaveCurrentCons"
        class="px-8 md:px-10 py-3 md:py-4 bg-primary text-white text-base font-medium rounded-lg hover:bg-hover transition shadow-md"
      >
        <span v-if="!loading">Consult Now</span>
        <span v-else>Initializing...</span>
      </button>
    </div>

    <!-- Fullscreen Overlay Loader -->
    <div v-if="loading" class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-200 bg-opacity-75">
      <svg class="animate-spin h-10 w-10 text-primary mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <span class="text-gray-800 text-lg font-medium">Initializing...</span>
    </div>
  </div>
</template>

<script>
import { ref, inject, onMounted } from "vue";

export default {
  props: {
    ifHaveCurrentCons: Boolean,
  },
  setup(props, { emit }) {
    const router = inject("$router");
    const axios = inject("$axios");
    const store = inject("$store");
    const swal = inject("$swal");
    const loading = ref(false);

    onMounted(() => {});
    const confirmConsult = async () => {
      const result = await swal.fire({
        title: "Start Consultation?",
        text: "Are you sure you want to proceed with your consultation?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Consult Now",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#16a34a",
        cancelButtonColor: "#6b7280",
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        // Call your consultation function here
        await initiateConsultationHandler();
      }
    };
    const initiateConsultationHandler = async () => {
      //Check if Active Status
      loading.value = true;
      let status = store.state.userMembershipStatus;
      if (status === "Active") {
        let checkIfOperationHours = await initiateCheckIfOperationHours();
        if (checkIfOperationHours.status === "OPEN") {
          let haveDoctorOnline = await initiateCheckIfOnlineDoctorHandler();
          if (haveDoctorOnline > 0) {
            consultationHandler();
          } else {
            loading.value = false;
            swal.fire({
              icon: "info",
              title: "We’ll Be With You Soon",
              html: `Thank you for reaching out.<br>
         Unfortunately, there are no doctors available to take consultations at this moment.<br>
         Please try again later.`,
              confirmButtonText: "Got It",
              confirmButtonColor: "#f59e0b",
            });
          }
        } else {
          loading.value = false;
          swal.fire({
            icon: "info",
            title: "We’ll Be With You Soon",
            html: `Thank you for reaching out!.<br>
             Our teleconsultation service is currently closed. Our operating hours are ${checkIfOperationHours.openTime} - ${checkIfOperationHours.closeTime} Philippine Standard Time. Please contact us again during these hours.`,
            confirmButtonText: "Got It",
            confirmButtonColor: "#f59e0b",
          });
        }
      } else {
        loading.value = false;
        swal.fire({
          icon: "info",
          title: "Notice",
          html: `
    We're unable to proceed with your request at this time.<br>
    For further assistance, please call <a href="tel:1234567890">IWC Hotline</a>(a1).<br>
    <strong>${process.env.VUE_APP_IWC_HOTLINE}</strong>
  `,
          confirmButtonText: "OK",
          confirmButtonColor: "#3b82f6", // info blue
        });
      }
    };

    const consultationHandler = async () => {
      try {
        loading.value = false;
        queuingAttemptHandler();
      } catch (error) {
        console.error(error);
      }
    };

    const queuingAttemptHandler = async () => {
      try {
        // Step 1: Send request to backend to join the queue
        const { data } = await axios.post("/api/patient/queuing", {
          controlnumber: store.state.userControlNumber,
          membername: store.state.userLogged,
        });

        const newTransactionId = data.data;

        router.push(`/portal/transaction/${newTransactionId}`);
      } catch (error) {
        console.error("❌ Queuing failed:", error);
      }
    };
    const initiateCheckIfCoveredHandler = async () => {
      try {
        const { data } = await axios.get(`/api/patient/checkifvalidate/benefit/${store.state.userControlNumber}`);
        return data[0].status;
      } catch (err) {
        console.error("Error checking coverage:", err);
        return false;
      }
    };
    const initiateCheckIfOperationHours = async () => {
      try {
        const { data } = await axios.get(`/api/patient/checkifvalidate/operation/hour`);
        return data[0];
      } catch (err) {
        console.error("Error checking coverage:", err);
        return false;
      }
    };
    const initiateCheckIfOnlineDoctorHandler = async () => {
      try {
        const { data } = await axios.get(`/api/patient/checkifvalidate/doctor`);
        return data[0].ifActive;
      } catch (err) {
        console.error("Error checking coverage:", err);
        return false;
      }
    };
    return {
      loading,
      confirmConsult,
      consultationHandler,
      queuingAttemptHandler,
      initiateConsultationHandler,
      initiateCheckIfCoveredHandler,
      initiateCheckIfOnlineDoctorHandler,
      initiateCheckIfOperationHours,
    };
  },
};
</script>
<style scoped>
.break-small-screen::before {
  content: "";
}

@media (max-width: 395px) {
  .break-small-screen::before {
    content: "\A"; /* newline */
    white-space: pre; /* preserve line break */
  }
}
</style>
